import { useState } from 'react';
import Field, { inputClass } from '@/components/base/Field';

const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const GRADES = [
  '초1', '초2', '초3', '초4', '초5', '초6',
  '중1', '중2', '중3',
  '고1', '고2', '고3',
];

const timeInputClass =
  'w-28 rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400';

export interface DayTimeValue {
  start: string;
  end: string;
}

export interface StudentFormValues {
  name: string;
  school: string;
  grade: string;
  parentContact: string;
  lessonLocation: string;
  targetType: string;
  targetSchool: string;
  desiredMajor: string;
  desiredCareer: string;
  classDays: string[];
  classTimes: Record<string, DayTimeValue>;
  conceptBook: string;
  calcBook: string;
  typeBook: string;
  advancedBook: string;
  completedBooks: string;
  tuitionFee: string;
  tuitionDueDay: string;
  firstLessonDate: string;
  goalDeadline: string;
  goalProgress: string;
}

export const emptyStudentForm: StudentFormValues = {
  name: '',
  school: '',
  grade: '중1',
  parentContact: '',
  lessonLocation: '',
  targetType: '고등학교',
  targetSchool: '',
  desiredMajor: '',
  desiredCareer: '',
  classDays: [],
  classTimes: {},
  conceptBook: '',
  calcBook: '',
  typeBook: '',
  advancedBook: '',
  completedBooks: '',
  tuitionFee: '',
  tuitionDueDay: '10',
  firstLessonDate: '',
  goalDeadline: '',
  goalProgress: '',
};

interface StudentFormProps {
  initialValues: StudentFormValues;
  submitLabel: string;
  submitIcon: string;
  submitting: boolean;
  error: string;
  onSubmit: (values: StudentFormValues) => void;
  onCancel: () => void;
}

export default function StudentForm({
  initialValues,
  submitLabel,
  submitIcon,
  submitting,
  error,
  onSubmit,
  onCancel,
}: StudentFormProps) {
  const [form, setForm] = useState<StudentFormValues>(initialValues);
  const [validationError, setValidationError] = useState('');

  const set = <K extends keyof StudentFormValues>(key: K, value: StudentFormValues[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleDay = (day: string) => {
    setForm((prev) => {
      const has = prev.classDays.includes(day);
      if (has) {
        const nextTimes = { ...prev.classTimes };
        delete nextTimes[day];
        return {
          ...prev,
          classDays: prev.classDays.filter((d) => d !== day),
          classTimes: nextTimes,
        };
      }
      return {
        ...prev,
        classDays: [...prev.classDays, day],
        classTimes: { ...prev.classTimes, [day]: prev.classTimes[day] ?? { start: '', end: '' } },
      };
    });
  };

  const setDayTime = (day: string, key: keyof DayTimeValue, value: string) => {
    setForm((prev) => ({
      ...prev,
      classTimes: {
        ...prev.classTimes,
        [day]: { ...(prev.classTimes[day] ?? { start: '', end: '' }), [key]: value },
      },
    }));
  };

  const selectedDays = DAYS.filter((d) => form.classDays.includes(d));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    if (!form.name.trim()) {
      setValidationError('학생 이름을 입력해주세요.');
      return;
    }
    onSubmit(form);
  };

  const shownError = validationError || error;

  return (
    <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-6">
      <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-user-line"></i>
          </span>
          기본 정보
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="학생 이름" required>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="예: 김민준"
              className={inputClass}
            />
          </Field>
          <Field label="학교">
            <input
              type="text"
              value={form.school}
              onChange={(e) => set('school', e.target.value)}
              placeholder="예: 대치중학교"
              className={inputClass}
            />
          </Field>
          <Field label="학년">
            <select
              value={form.grade}
              onChange={(e) => set('grade', e.target.value)}
              className={inputClass}
            >
              {GRADES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </Field>
          <Field label="학부모 연락처" hint="학부모님께 연락할 번호를 적어두세요.">
            <input
              type="tel"
              value={form.parentContact}
              onChange={(e) => set('parentContact', e.target.value)}
              placeholder="예: 010-1234-5678"
              className={inputClass}
            />
          </Field>
          <Field label="교습 장소" hint="수업이 진행되는 장소를 적어두세요.">
            <input
              type="text"
              value={form.lessonLocation}
              onChange={(e) => set('lessonLocation', e.target.value)}
              placeholder="예: 대치동 스터디카페 3번룸"
              className={inputClass}
            />
          </Field>
          <Field label="목표 유형">
            <select
              value={form.targetType}
              onChange={(e) => set('targetType', e.target.value)}
              className={inputClass}
            >
              <option value="고등학교">고등학교</option>
              <option value="대학">대학</option>
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="목표 학교 / 대학">
              <input
                type="text"
                value={form.targetSchool}
                onChange={(e) => set('targetSchool', e.target.value)}
                placeholder="예: 서울과학고등학교"
                className={inputClass}
              />
            </Field>
          </div>
          <Field label="희망 학과" hint="가고 싶은 대학의 학과를 적어두세요.">
            <input
              type="text"
              value={form.desiredMajor}
              onChange={(e) => set('desiredMajor', e.target.value)}
              placeholder="예: 컴퓨터공학과"
              className={inputClass}
            />
          </Field>
          <Field label="희망 진로" hint="장래 희망이나 목표하는 직업을 적어두세요.">
            <input
              type="text"
              value={form.desiredCareer}
              onChange={(e) => set('desiredCareer', e.target.value)}
              placeholder="예: 소프트웨어 개발자"
              className={inputClass}
            />
          </Field>
          <Field label="첫 수업일">
            <input
              type="date"
              value={form.firstLessonDate}
              onChange={(e) => set('firstLessonDate', e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="목표 진도 달성일" hint="언제까지 목표 진도를 끝낼지 정해두세요.">
            <input
              type="date"
              value={form.goalDeadline}
              onChange={(e) => set('goalDeadline', e.target.value)}
              className={inputClass}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="목표 진도 내용"
              hint="달성하려는 목표 진도를 적어두면 학부모님께도 함께 보여드려요."
            >
              <textarea
                value={form.goalProgress}
                onChange={(e) => set('goalProgress', e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="예: 공통수학1 전 범위 + 유형서 1회독 완료, 모의고사 3등급 진입"
                className={`${inputClass} resize-none`}
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-calendar-schedule-line"></i>
          </span>
          수업 일정
        </h2>
        <div className="mt-5 flex flex-col gap-4">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground-800">수업 요일</p>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  className={`flex h-10 w-10 items-center justify-center rounded-md text-sm font-semibold transition-colors ${
                    form.classDays.includes(d)
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {selectedDays.length === 0 ? (
            <p className="rounded-lg border border-dashed border-background-300 px-4 py-3 text-xs text-foreground-500">
              요일을 선택하면 요일별로 수업 시작·종료 시간을 입력할 수 있어요.
            </p>
          ) : (
            <div className="flex flex-col gap-2.5 rounded-lg border border-background-200 bg-background-100 p-3 md:p-4">
              <p className="flex items-center gap-1.5 text-xs font-medium text-foreground-600">
                <i className="ri-time-line"></i>
                요일마다 시간이 달라도 각각 입력할 수 있어요.
              </p>
              {selectedDays.map((d) => (
                <div key={d} className="flex flex-wrap items-center gap-2">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-500 text-sm font-semibold text-background-50">
                    {d}
                  </span>
                  <input
                    type="time"
                    value={form.classTimes[d]?.start ?? ''}
                    onChange={(e) => setDayTime(d, 'start', e.target.value)}
                    className={timeInputClass}
                  />
                  <span className="text-foreground-500">~</span>
                  <input
                    type="time"
                    value={form.classTimes[d]?.end ?? ''}
                    onChange={(e) => setDayTime(d, 'end', e.target.value)}
                    className={timeInputClass}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
            <i className="ri-book-2-line"></i>
          </span>
          교재
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="개념 진도 교재">
            <input
              type="text"
              value={form.conceptBook}
              onChange={(e) => set('conceptBook', e.target.value)}
              placeholder="예: 개념원리 공통수학1"
              className={inputClass}
            />
          </Field>
          <Field label="연산 교재">
            <input
              type="text"
              value={form.calcBook}
              onChange={(e) => set('calcBook', e.target.value)}
              placeholder="예: 연산으로 강해지는 수학"
              className={inputClass}
            />
          </Field>
          <Field label="유형서 교재">
            <input
              type="text"
              value={form.typeBook}
              onChange={(e) => set('typeBook', e.target.value)}
              placeholder="예: 유형+심화 고쟁이"
              className={inputClass}
            />
          </Field>
          <Field label="심화서 교재">
            <input
              type="text"
              value={form.advancedBook}
              onChange={(e) => set('advancedBook', e.target.value)}
              placeholder="예: 일품 수학"
              className={inputClass}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="한번 다 푼 교재"
              hint="이미 끝낸 교재도 적어두면 이후 복습·진도 관리에 도움이 돼요."
            >
              <textarea
                value={form.completedBooks}
                onChange={(e) => set('completedBooks', e.target.value)}
                maxLength={500}
                rows={2}
                placeholder="예: 개념원리 중1-1 (2025.06 완료), 쎈 중1-1 전체"
                className={`${inputClass} resize-none`}
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-wallet-3-line"></i>
          </span>
          교습비
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="교습비 (원)">
            <input
              type="number"
              min={0}
              value={form.tuitionFee}
              onChange={(e) => set('tuitionFee', e.target.value)}
              placeholder="예: 400000"
              className={inputClass}
            />
          </Field>
          <Field label="결제일 (매월)">
            <select
              value={form.tuitionDueDay}
              onChange={(e) => set('tuitionDueDay', e.target.value)}
              className={inputClass}
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>
                  매월 {n}일
                </option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      {shownError && (
        <div className="flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-4 py-2.5 text-sm text-accent-800">
          <i className="ri-error-warning-line"></i>
          {shownError}
        </div>
      )}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 whitespace-nowrap"
        >
          취소
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              저장 중...
            </>
          ) : (
            <>
              <i className={submitIcon}></i>
              {submitLabel}
            </>
          )}
        </button>
      </div>
    </form>
  );
}